import { Inject, Injectable } from "@angular/core";
import { ProductConfiguration } from "../Models/product-configuration";
import { AbstractService } from "./abstract-service.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { APP_CONFIG, AppConfig } from "./config";
import { Observable } from "rxjs";
import { Response } from "../Models/response";
import { Page } from "../Models/page";
import { map } from "rxjs/operators";
import { Product } from "../Models/product";

@Injectable({
  providedIn: "root",
})
export class ProductConfigurationService extends AbstractService<
  ProductConfiguration,
  number
> {
  url: string;
  url_: string;

  constructor(
    protected http: HttpClient,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
  ) {
    super(http);
    this.url = `${this.appConfig.apiEndpoint}/new/product-configurations`;
    this.url_ = `${this.appConfig.apiEndpoint}/new/products`;
  }

  public getByProductId(
    id: number,
  ): Observable<Response<ProductConfiguration[]>> {
    const url = `${this.url}/product/${id}`;
    return this.http.get<Response<ProductConfiguration[]>>(url);
  }

  public getProductByProductId(id: number): Observable<any> {
    const url = `${this.url_}/guichet/${id}`;
    return this.http.get<any>(url);
  }

  public getByConfigurationId(id: number): Observable<ProductConfiguration> {
    const url = `${this.url}/${id}`;
    return this.http.get<ProductConfiguration>(url).pipe(
      map((response: ProductConfiguration) => {
        if (response) {
          return response;
        } else {
          throw new Error();
        }
      }),
    );
  }

  public trendyProducts(): Observable<ProductConfiguration[]> {
    const url = `${this.url_}/trending/pages`;
    return this.http
      .get<Response<any>>(url)
      .pipe(
        map((response) =>
          response.body.content.map((e: any) => e.configuration),
        ),
      );
  }

  public bestSellers(): Observable<any[]> {
    const url = `${this.url_}/best-sellers/pages`;
    return this.http
      .get<Response<any>>(url)
      .pipe(
        map((response) =>
          response.body.content.map((e: any) => e.configuration),
        ),
      );
  }

  public newArrival(): Observable<any[]> {
    const url = `${this.url_}/new/new-arrival/pages`;

    return this.http.get<Response<any>>(url).pipe(
      map((response) => {
        const data = response.body.content.map((e: any) => e.configuration);
        return data;
      }),
    );
  }

  public relatedConfigurations(
    categoryId: number,
    page = 0,
    size = 10,
  ): Observable<ProductConfiguration[]> {
    const url = `${this.url}/relatedConfigurations/${categoryId}`;
    let params = new HttpParams()
      .set("page", page.toString())
      .set("size", size.toString());
    return this.http
      .get<{ content: ProductConfiguration[] }>(url, { params })
      .pipe(
        map((response) => {
          if (response && Array.isArray(response.content)) {
            return response.content;
          } else {
            throw new Error("Invalid response structure");
          }
        }),
      );
  }

  public getByFilter(
    filter: {
      minPrice?: string;
      maxPrice?: string;
      brands?: string[];
      orderBy?: string;
    },
    category: string,
    page: number = 0,
    size: number = 5,
  ): Observable<Response<Page<ProductConfiguration>>> {
    let params = new HttpParams()
      .append("category", category)
      .append("page", page.toString())
      .append("size", size.toString());
    params = filter.maxPrice
      ? params.append("max-price", filter.maxPrice)
      : params;
    params = filter.minPrice
      ? params.append("min-price", filter.minPrice)
      : params;
    params = filter.brands
      ? params.append("brands", filter.brands.toString())
      : params;
    params = filter.orderBy ? params.append("orderBy", filter.orderBy) : params;

    const url = `${this.url}/filter`;
    return this.http
      .get<Response<Page<ProductConfiguration>>>(url, { params })
      .pipe(
        map((response: Response<Page<ProductConfiguration>>) => {
          if (response.body) {
            return response;
          } else {
            throw new Error();
          }
        }),
      );
  }

  public getTrending(
    pageNumber: number = 0,
    pageSize: number = 10,
  ): Observable<Response<Page<ProductConfiguration>>> {
    const options = {
      params: new HttpParams()
        .append("pageNumber", pageNumber.toString())
        .append("pageSize", pageSize.toString()),
    };
    const url = `${this.url}/trending-displayed`;
    return this.http.get<Response<Page<ProductConfiguration>>>(url, options);
  }

  public getBestSellers(
    pageNumber: number = 0,
    pageSize: number = 5,
  ): Observable<Response<Page<ProductConfiguration>>> {
    const options = {
      params: new HttpParams()
        .append("pageNumber", pageNumber.toString())
        .append("pageSize", pageSize.toString()),
    };
    const url = `${this.url}/best-sellers-displayed`;
    return this.http.get<Response<Page<ProductConfiguration>>>(url, options);
  }

  public getNew(
    pageNumber: number = 0,
    pageSize: number = 8,
  ): Observable<Page<ProductConfiguration>> {
    const options = {
      params: new HttpParams()
        .append("pageNumber", pageNumber.toString())
        .append("pageSize", pageSize.toString()),
    };
    const url = `${this.url}/guichet/new`;
    return this.http.get<Page<ProductConfiguration>>(url, options);
  }

  public getBestRated(
    page: number = 0,
    size: number = 5,
  ): Observable<Response<Page<ProductConfiguration>>> {
    const options = {
      params: new HttpParams()
        .append("page", page.toString())
        .append("size", size.toString()),
    };
    const url = `${this.url}/guichet/best-rated`;
    return this.http.get<Response<Page<ProductConfiguration>>>(url, options);
  }
  public getBestPromotion(pageNo = 0, pageSize = 8): Observable<any> {
    const url = `${this.url}`;
    let params = new HttpParams()
      .set("pageNo", pageNo.toString())
      .set("pageSize", pageSize.toString());
    return this.http
      .get(url, { params })
      .pipe(map((response: any) => response.content));
  }

  public getProductList(pageNo = 0, pageSize = 8): Observable<Product[]> {
    const url = `${this.url}/pages`;
    const params = new HttpParams()
      .set("pageNo", pageNo.toString())
      .set("pageSize", pageSize.toString());

    return this.http.get<Product[]>(url, { params });
  }

  public getFlashSale(
    pageNumber: number,
    pageSize: number,
  ): Observable<Response<Page<ProductConfiguration>>> {
    const options = {
      params: new HttpParams()
        .append("pageNo", pageNumber.toString())
        .append("pageSize", pageSize.toString()),
    };
    const url = `${this.url}/guichet/ventFLASH`;
    return this.http.get<Response<Page<ProductConfiguration>>>(url, options);
  }

  public getProductAccesories(
    id: number,
  ): Observable<Response<ProductConfiguration[]>> {
    const url = `${this.url}/accessory?id=${id}`;
    return this.http.get<Response<ProductConfiguration[]>>(url);
  }
}
